import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ToolSidebar from "../../utils/ToolSideBar";
import { API_URL, authFetch } from "../../utils/auth";
import "../../../css/superuser/StaffDirectoryPage.css";

const statLabels = [
  ["total", "Total"],
  ["new", "New"],
  ["under_review", "Under Review"],
  ["interviews", "Interviews"],
  ["offers", "Offers"],
  ["hired", "Hired"],
  ["rejected", "Rejected"],
];

function QuestionField({ question }) {
  const name = `question_${question.id}`;
  const required = Boolean(question.required);
  const choices = question.choices || [];

  if (question.question_type === "textarea") return <textarea name={name} required={required} rows="4" />;
  if (["select", "radio"].includes(question.question_type)) {
    return <select name={name} required={required}><option value="">Choose one</option>{choices.map((choice) => <option key={choice} value={choice}>{choice}</option>)}</select>;
  }
  if (question.question_type === "checkbox" && choices.length) {
    return <div className="staff-checkbox-list">{choices.map((choice) => <label key={choice}><input type="checkbox" name={name} value={choice} /> {choice}</label>)}</div>;
  }
  if (question.question_type === "yes_no") return <select name={name} required={required}><option value="">Choose one</option><option value="Yes">Yes</option><option value="No">No</option></select>;
  const type = question.question_type === "number" ? "number" : ["email", "url"].includes(question.question_type) ? question.question_type : "text";
  return <input type={type} name={name} required={required} />;
}

export default function JobApplicationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ results: [], stats: {}, jobs: [], status_choices: [], source_choices: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [createOptions, setCreateOptions] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [source, setSource] = useState("indeed");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const filters = {
    q: searchParams.get("q") || "",
    status: searchParams.get("status") || "",
    job: searchParams.get("job") || "",
    source: searchParams.get("source") || "",
    date_from: searchParams.get("date_from") || "",
  };

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const query = searchParams.toString();
        const response = await authFetch(`${API_URL}/ops/job-applications/${query ? `?${query}` : ""}`);
        if (!response.ok) throw new Error("Unable to load applications.");
        setData(await response.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [searchParams]);

  async function openForm() {
    setShowForm(true);
    setFormError("");
    if (createOptions) return;
    try {
      const response = await authFetch(`${API_URL}/ops/job-applications/create-options/`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.detail || "Unable to load application form.");
      setCreateOptions(payload);
      setSelectedJobId(String(payload.jobs?.[0]?.id || ""));
      setSource(payload.default_source || "indeed");
    } catch (err) {
      setFormError(err.message);
    }
  }

  function updateFilter(name, value) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(name, value);
    else next.delete(name);
    next.delete("page");
    setSearchParams(next);
  }

  async function submitApplication(event) {
    event.preventDefault();
    setFormError("");
    setSubmitting(true);
    try {
      const formData = new FormData(event.currentTarget);
      const missingCheckbox = selectedJob?.questions?.find((question) => question.required && question.question_type === "checkbox" && question.choices?.length && formData.getAll(`question_${question.id}`).length === 0);
      if (missingCheckbox) {
        setFormError(`Please answer: ${missingCheckbox.question}`);
        return;
      }
      const response = await authFetch(`${API_URL}/ops/job-applications/`, { method: "POST", body: formData });
      const payload = await response.json();
      if (!response.ok) {
        setFormError(payload?.errors?.join(" ") || payload?.detail || "Application could not be created.");
        return;
      }
      navigate(`/job-applications/${payload.id}`);
    } catch (err) {
      setFormError(err.message || "Application could not be created.");
    } finally {
      setSubmitting(false);
    }
  }

  const selectedJob = createOptions?.jobs?.find((job) => String(job.id) === String(selectedJobId));
  const sourceChoices = createOptions?.sources || data.source_choices || [];

  return (
    <div className="staff-shell">
      <ToolSidebar active="job-applications" />
      <main className="staff-page">
        <header className="staff-header">
          <span>Hiring</span>
          <h1>Job Applications</h1>
          <p>Review public and external applicants in the same hiring workflow.</p>
          <button type="button" className="btn btn-primary" onClick={openForm}>+ Add Application</button>
        </header>

        {showForm && (
          <section className="staff-card staff-panel-pad">
            <div className="staff-card-header flush">
              <h2>Add External Application</h2>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Close</button>
            </div>
            {formError && <p className="staff-alert danger">{formError}</p>}
            <form className="staff-form-stack" onSubmit={submitApplication}>
              <div className="staff-detail-grid compact">
                <label>First name<input name="first_name" required /></label>
                <label>Last name<input name="last_name" required /></label>
              </div>
              <div className="staff-detail-grid compact">
                <label>Email<input type="email" name="email" required /></label>
                <label>Phone<input name="phone" /></label>
              </div>
              <div className="staff-detail-grid compact">
                <label>Job<select name="job_id" value={selectedJobId} onChange={(e) => setSelectedJobId(e.target.value)} required>{createOptions?.jobs?.map((job) => <option key={job.id} value={job.id}>{job.title}</option>)}</select></label>
                <label>Source<select name="application_source" value={source} onChange={(e) => setSource(e.target.value)} required>{sourceChoices.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}</select></label>
              </div>
              {source === "other" && <label>Source note<input name="application_source_note" /></label>}
              <label>Resume<input type="file" name="resume" accept=".pdf,.doc,.docx" /></label>
              {selectedJob?.questions?.length ? <h3>{selectedJob.title} Questions</h3> : null}
              {selectedJob?.questions?.map((question) => <label key={question.id}>{question.question}{question.required ? " *" : ""}<QuestionField question={question} /></label>)}
              <button className="btn btn-primary" disabled={submitting}>{submitting ? "Creating..." : "Create Application"}</button>
            </form>
          </section>
        )}

        <section className="staff-stats">
          {statLabels.map(([key, label]) => <div className="staff-stat" key={key}><strong>{data.stats?.[key] ?? 0}</strong><span>{label}</span></div>)}
        </section>

        <section className="staff-card">
          <div className="staff-filterbar application-filters">
            <input value={filters.q} onChange={(e) => updateFilter("q", e.target.value)} placeholder="Search name, email, phone" />
            <select value={filters.status} onChange={(e) => updateFilter("status", e.target.value)}><option value="">All stages</option>{data.status_choices?.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}</select>
            <select value={filters.job} onChange={(e) => updateFilter("job", e.target.value)}><option value="">All jobs</option>{data.jobs?.map((job) => <option key={job.id} value={job.id}>{job.title}</option>)}</select>
            <select value={filters.source} onChange={(e) => updateFilter("source", e.target.value)}><option value="">All sources</option>{data.source_choices?.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}</select>
            <input type="date" value={filters.date_from} onChange={(e) => updateFilter("date_from", e.target.value)} />
          </div>

          {loading ? <p className="staff-empty">Loading applications...</p> : error ? <p className="staff-empty">{error}</p> : (
            <div className="staff-table-wrap">
              <table className="staff-table">
                <thead><tr><th>Applicant</th><th>Job</th><th>Source</th><th>Submitted</th><th>Stage</th><th>Resume</th><th>Reviewer</th><th>Action</th></tr></thead>
                <tbody>
                  {data.results.length === 0 ? <tr><td colSpan="8"><p className="staff-empty inline">No applications match those filters.</p></td></tr> : data.results.map((app) => (
                    <tr key={app.id} className="staff-clickable-row" tabIndex="0" role="link" onClick={() => navigate(`/job-applications/${app.id}`)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); navigate(`/job-applications/${app.id}`); } }}>
                      <td><Link className="staff-strong-link" to={`/job-applications/${app.id}`} onClick={(event) => event.stopPropagation()}>{app.name}</Link><small>{app.email}</small></td>
                      <td>{app.job_title}</td>
                      <td>{app.source_label || app.source || "SCD Website"}</td>
                      <td>{new Date(app.submitted_at).toLocaleDateString()}</td>
                      <td><span className="staff-pill">{app.stage_label}</span></td>
                      <td>{app.resume_available ? "Yes" : "No"}</td>
                      <td>{app.assigned_reviewer}</td>
                      <td><button type="button" className="staff-review-button" onClick={(event) => { event.stopPropagation(); navigate(`/job-applications/${app.id}`); }}>Review</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && !error && data.num_pages > 1 && <div className="staff-pagination"><button type="button" disabled={data.page <= 1} onClick={() => updateFilter("page", String(data.page - 1))}>Previous</button><span>Page {data.page} of {data.num_pages}</span><button type="button" disabled={data.page >= data.num_pages} onClick={() => updateFilter("page", String(data.page + 1))}>Next</button></div>}
        </section>
      </main>
    </div>
  );
}
